import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, FloorLabel, Img } from "../motion/Reveal";
import { t } from "../../lib/i18n";

/**
 * Services as an editorial spread: one large photograph on the left, a clean index of the six disciplines
 * on the right. Pointing at (or focusing) a line swaps the photograph and unfolds a one-line explanation
 * beneath the name. All six photographs are stacked in the frame and only the active one is opaque, so a
 * swap is a composited opacity change – nothing is mounted, laid out or decoded on hover.
 */
export const ServicesFloor = ({ services = [] }) => {
  const [active, setActive] = useState(0);
  const list = services.slice(0, 6);
  const current = list[active] || list[0];

  return (
    <section data-testid="floor-tjenester" className="bg-white">
      <div className="wrap floor">
        <Reveal className="grid-12 gap-y-6 items-end">
          <div className="col-span-12 lg:col-span-7">
            <FloorLabel>{t("Tjenester")}</FloorLabel>
            <h2 className="mt-4 t-h2 max-w-[16ch]">{t("Seks fagområder. Én samlet kapasitet.")}</h2>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:col-start-9">
            <p className="t-body text-muted max-w-[40ch]">{t("Fra engineering og fabrikasjon til elektro, rør og vedlikehold. Selskapene leverer hver for seg – og sammen når oppgaven krever flere fag.")}</p>
            <Link to="/tjenester" data-testid="services-see-all" className="link-arrow mt-5 t-small">{t("Se alle tjenester")} <ArrowRight size={16} /></Link>
          </div>
        </Reveal>

        <div className="mt-14 md:mt-20 grid-12 gap-y-10 lg:items-stretch">
          {/* The photograph – one frame, six stacked pictures, the active one opaque */}
          <Reveal soft className="col-span-12 lg:col-span-7">
            <div data-testid="services-stage" data-active-slug={current?.slug} className="relative aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[560px] overflow-hidden bg-surface">
              {list.map((s, i) => (
                <Img
                  key={s.slug}
                  src={s.image}
                  alt={i === active ? s.name : ""}
                  sizes="(min-width:1440px) 720px, (min-width:1024px) 58vw, 100vw"
                  fade={false}
                  eager={i === 0}
                  data-testid={`services-stage-img-${i}`}
                  aria-hidden={i !== active}
                  className={`svc-stage-img absolute inset-0 h-full w-full object-cover ${i === active ? "is-active" : ""}`}
                />
              ))}
            </div>
          </Reveal>

          {/* The index */}
          <Reveal delay={100} as="ol" data-testid="services-index" className="col-span-12 lg:col-span-5 lg:col-start-8 border-t border-line self-start">
            {list.map((s, i) => {
              const on = i === active;
              return (
                <li key={s.slug} className={`svc-row border-b border-line ${on ? "is-active" : ""}`} data-active={on ? "true" : "false"}>
                  <Link
                    to={`/tjenester/${s.slug}`}
                    data-testid={`service-item-${i}`}
                    className="group grid grid-cols-[2.25rem_1fr_auto] items-start gap-x-4 py-5 md:py-[22px] outline-offset-[-3px]"
                    onMouseEnter={() => setActive(i)}
                    onFocus={() => setActive(i)}
                  >
                    <span className="tabular pt-[0.35em] text-[13px] font-semibold tracking-[0.06em] text-muted">{String(i + 1).padStart(2, "0")}</span>
                    <span className="min-w-0">
                      <span className={`svc-row-name block text-[clamp(1.25rem,1.6vw,1.625rem)] font-medium leading-[1.2] tracking-[-0.02em] ${on ? "text-blue" : "text-ink"}`}>{s.name}</span>
                      <span className="svc-row-x">
                        <span className="svc-row-x-in">
                          {s.lead && <span className="block pt-2.5 text-[15px] leading-[1.5] text-muted max-w-[38ch]">{s.lead}</span>}
                          <span className="link-arrow mt-3 text-[14px]">{t("Utforsk")} <ArrowRight size={15} aria-hidden="true" /></span>
                        </span>
                      </span>
                    </span>
                    <ArrowRight size={18} strokeWidth={1.5} className={`mt-[0.3em] transition-[transform,color] duration-200 ${on ? "translate-x-0 text-blue" : "-translate-x-1 text-muted/60"}`} />
                  </Link>
                </li>
              );
            })}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
